import { useEffect, useRef } from "react";
import { paths } from "../app/paths.js";

export interface ShortcutGroup {
  readonly title: string;
  readonly items: ReadonlyArray<{ readonly keys: ReadonlyArray<string>; readonly label: string }>;
}

export function modifierLabel(): string {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent) ? "⌘" : "Strg";
}

export function shortcutGroups(mod: string): ReadonlyArray<ShortcutGroup> {
  return [
    {
      title: "Allgemein",
      items: [
        { keys: [mod, "K"], label: "Suche und Befehle" },
        { keys: [mod, "⇧", "L"], label: "Tresor sofort sperren" },
        { keys: ["N"], label: "Neuer Eintrag" },
        { keys: ["/"], label: "Suchen" },
        { keys: ["?"], label: "Diese Übersicht" }
      ]
    },
    {
      title: "Navigation",
      items: [
        { keys: ["G", "D"], label: "Übersicht" },
        { keys: ["G", "V"], label: "Alle Einträge" },
        { keys: ["G", "F"], label: "Favoriten" },
        { keys: ["G", "G"], label: "Passwort-Generator" },
        { keys: ["G", "T"], label: "Teams" },
        { keys: ["G", "S"], label: "Einstellungen" }
      ]
    },
    {
      title: "Eintragsliste",
      items: [
        { keys: ["↑", "↓"], label: "Zwischen Einträgen wechseln" },
        { keys: ["C"], label: "Passwort kopieren" },
        { keys: ["U"], label: "Benutzernamen kopieren" },
        { keys: ["T"], label: "2FA-Code kopieren" },
        { keys: ["O"], label: "Webadresse öffnen" },
        { keys: [mod, "S"], label: "Speichern (im Editor)" }
      ]
    },
    {
      title: "Suche",
      items: [
        { keys: ["↵"], label: "Eintrag öffnen" },
        { keys: ["⇧", "↵"], label: "Passwort kopieren" },
        { keys: ["Alt", "↵"], label: "Benutzernamen kopieren" }
      ]
    }
  ];
}

const GO_TO: Readonly<Record<string, string>> = {
  d: paths.dashboard,
  v: paths.vault,
  f: "/vault?view=favorites",
  g: paths.generator,
  t: paths.teams,
  s: paths.settings
};

const SEQUENCE_WINDOW_MS = 1200;

export function isEditableTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));
}

function modalOpen(): boolean {
  return document.querySelector('[aria-modal="true"]') !== null;
}

export interface ShortcutHandlers {
  readonly togglePalette: () => void;
  readonly openPalette: () => void;
  readonly openHelp: () => void;
  readonly lock: () => void;
  readonly navigate: (to: string) => void;
}

/** App-wide keyboard shortcuts. Single keys are ignored while typing or while a dialog is open. */
export function useGlobalShortcuts(handlers: ShortcutHandlers): void {
  const ref = useRef(handlers);

  useEffect((): void => {
    ref.current = handlers;
  }, [handlers]);

  useEffect((): (() => void) => {
    let pendingG = 0;

    function onKey(event: KeyboardEvent): void {
      const h = ref.current;
      const key = event.key.toLowerCase();
      const mod = event.metaKey || event.ctrlKey;
      if (mod && !event.shiftKey && !event.altKey && key === "k") {
        event.preventDefault();
        h.togglePalette();
        return;
      }
      if (mod && event.shiftKey && !event.altKey && key === "l") {
        event.preventDefault();
        h.lock();
        return;
      }
      if (mod || event.altKey || event.repeat || isEditableTarget(event.target) || modalOpen()) {
        return;
      }
      if (pendingG > Date.now()) {
        pendingG = 0;
        const target = GO_TO[key];
        if (target !== undefined) {
          event.preventDefault();
          h.navigate(target);
        }
        return;
      }
      if (key === "g") {
        pendingG = Date.now() + SEQUENCE_WINDOW_MS;
      } else if (event.key === "?") {
        event.preventDefault();
        h.openHelp();
      } else if (key === "n" && !event.shiftKey) {
        event.preventDefault();
        h.navigate(paths.vaultNew);
      } else if (event.key === "/") {
        event.preventDefault();
        const search = document.getElementById("vault-search");
        if (search !== null) {
          search.focus();
        } else {
          h.openPalette();
        }
      }
    }

    window.addEventListener("keydown", onKey);
    return (): void => window.removeEventListener("keydown", onKey);
  }, []);
}
